import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const Coin = ({ style }) => (
    <div
        className="absolute animate-bounce"
        style={{
          ...style,
          color: '#FFC000', // 更深的金色
          textShadow: '0 0 5px rgba(255, 180, 0, 0.8)',
          backgroundImage: 'radial-gradient(circle, #FFD700 30%, #FF8C00 100%)', // 更鲜明的渐变
          backgroundClip: 'text',
          WebkitBackgroundClip: 'text',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: style.fontSize,
          height: style.fontSize,
          borderRadius: '50%',
          backgroundColor: '#FFC000', // 更深的金色背景
          boxShadow: '0 0 10px rgba(255, 165, 0, 0.9), inset 0 0 5px rgba(255, 215, 0, 1)', // 增加内阴影
          fontSize: style.fontSize,
        }}
    >
    <span style={{
      position: 'absolute',
      left: '50%',
      top: '50%',
      transform: 'translate(-50%, -50%) rotate(0deg)',
      color: '#8B4513', // 更深的褐色
      fontSize: '70%',
      fontWeight: 'bold'
    }}>
      ¥
    </span>
    </div>
);

const IncomeCalculator = () => {
  const { t } = useTranslation();
  const [yearlyIncome, setYearlyIncome] = useState(100000000); // 默认年收入为1亿
  const [formattedIncome, setFormattedIncome] = useState('100,000,000'); // 格式化后的年收入显示
  const [currentTime, setCurrentTime] = useState(new Date());
  const [startTime, setStartTime] = useState(new Date());
  const [isRunning, setIsRunning] = useState(true);
  const [coins, setCoins] = useState([]);
  const [pausedEarnings, setPausedEarnings] = useState(0); // 存储暂停时的累计收入
  const containerRef = useRef(null);
  const coinIdCounter = useRef(0);

  // 计算各个时间单位的收入
  const calculateIncome = () => {
    // 避免除以零错误
    if (!yearlyIncome) return { perSecond: 0, perMinute: 0, perHour: 0, perDay: 0 };

    const perYear = parseFloat(yearlyIncome);
    const perDay = perYear / 365;
    const perHour = perDay / 24;
    const perMinute = perHour / 60;
    const perSecond = perMinute / 60;

    return {
      perSecond,
      perMinute,
      perHour,
      perDay
    };
  };

  // 计算自开始时间以来已经赚到的收入
  const calculateEarnedSoFar = () => {
    const income = calculateIncome();

    if (isRunning) {
      const secondsPassed = (currentTime - startTime) / 1000;
      return pausedEarnings + (income.perSecond * secondsPassed);
    } else {
      // 当暂停时，返回已累计的收入
      return pausedEarnings;
    }
  };

  // 格式化数字为千位分隔符格式
  const formatNumberWithCommas = (num) => {
    return num.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  };

  // 处理收入输入变化
  const handleIncomeChange = (e) => {
    // 移除所有非数字字符（包括逗号），但保留小数点
    let rawValue = e.target.value.replace(/[^0-9.]/g, '');

    // 如果是有效数字，更新状态
    if (rawValue !== '' && !isNaN(parseFloat(rawValue))) {
      setYearlyIncome(rawValue);
      setFormattedIncome(formatNumberWithCommas(rawValue));
    } else if (rawValue === '') {
      // 如果输入为空，允许清空
      setYearlyIncome('');
      setFormattedIncome('');
    }
  };

  // 重置计时器和金币
  const resetTimer = () => {
    setStartTime(new Date());
    setCurrentTime(new Date());
    setPausedEarnings(0); // 重置暂停时的累计收入
    setCoins([]);
    // 重置年收入为默认值
    setYearlyIncome(100000000);
    setFormattedIncome('100,000,000');
    if (!isRunning) {
      setIsRunning(true); // 如果当前是暂停状态，重置后自动开始计时
    }
  };

  // 暂停/继续计时
  const toggleTimer = () => {
    const now = new Date();

    if (isRunning) {
      // 如果当前正在运行，则暂停并保存当前累计收入
      setPausedEarnings(calculateEarnedSoFar());
      // 保存暂停时的时间
      setCurrentTime(now);
    } else {
      // 如果从暂停状态恢复，重新设置开始时间
      // 将开始时间设置为当前时间，这样新的计算将从0开始
      // 因为我们已经将之前累计的收入保存在pausedEarnings中
      setStartTime(now);
      setCurrentTime(now);
    }
    setIsRunning(!isRunning);
  };

  // 创建新金币
  const createCoin = () => {
    if (!containerRef.current || !isRunning) return;

    const containerWidth = containerRef.current.offsetWidth;
    const containerHeight = containerRef.current.offsetHeight;

    // 随机位置
    const x = Math.random() * (containerWidth - 30);
    const y = -30; // 从顶部开始下落

    // 随机终点位置
    const finalY = containerHeight * 0.5 + Math.random() * (containerHeight * 0.4);

    // 随机大小和旋转
    const size = 20 + Math.random() * 15;
    const rotation = Math.random() * 360;

    // 随机下落时间 (2-4秒)
    const fallDuration = 2 + Math.random() * 2;

    const newCoin = {
      id: coinIdCounter.current++,
      style: {
        left: `${x}px`,
        top: `${y}px`,
        fontSize: `${size}px`,
        transform: `rotate(${rotation}deg)`,
        transition: `top ${fallDuration}s cubic-bezier(0.4, 0, 0.2, 1)`,
        opacity: 0.9,
        zIndex: 10,
      },
      finalStyle: {
        top: `${finalY}px`,
      }
    };

    setCoins(prev => [...prev, newCoin]);

    // 延迟更新金币的位置
    setTimeout(() => {
      setCoins(prev => prev.map(coin =>
          coin.id === newCoin.id
              ? { ...coin, style: { ...coin.style, ...coin.finalStyle } }
              : coin
      ));
    }, 50);
  };

  // 实时更新当前时间和创建金币
  useEffect(() => {
    let timer;
    let coinTimer;

    if (isRunning) {
      // 更新时间
      timer = setInterval(() => {
        setCurrentTime(new Date());
      }, 100); // 更新频率为0.1秒

      // 创建金币
      coinTimer = setInterval(() => {
        const income = calculateIncome();

        // 根据收入调整金币生成速率
        if (income.perSecond > 0) {
          // 收入越高，金币生成越快
          const rate = Math.max(1, Math.min(10, Math.floor(income.perSecond)));

          // 一次可能创建多个金币
          for (let i = 0; i < Math.min(3, rate); i++) {
            if (Math.random() < 0.7) { // 随机控制生成概率
              createCoin();
            }
          }
        }
      }, 500); // 每0.5秒检查是否需要创建金币
    }

    return () => {
      if (timer) clearInterval(timer);
      if (coinTimer) clearInterval(coinTimer);
    };
  }, [isRunning, yearlyIncome]);

  const income = calculateIncome();
  const earnedSoFar = calculateEarnedSoFar();

  // 格式化金额为货币格式
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY',
      minimumFractionDigits: 2,
      maximumFractionDigits: 6
    }).format(amount);
  };

  // 禁用页面滚动和滚轮事件
  useEffect(() => {
    // 保存原始样式
    const originalStyle = window.getComputedStyle(document.body).overflow;

    // 禁用滚动
    document.body.style.overflow = 'hidden';

    // 禁用滚轮事件
    const preventWheel = (e) => {
      e.preventDefault();
    };

    // 添加事件监听器，捕获阶段
    window.addEventListener('wheel', preventWheel, { passive: false });
    window.addEventListener('touchmove', preventWheel, { passive: false });

    // 组件卸载时恢复原始样式并移除事件监听器
    return () => {
      document.body.style.overflow = originalStyle;
      window.removeEventListener('wheel', preventWheel);
      window.removeEventListener('touchmove', preventWheel);
    };
  }, []);

  return (
      <div
          ref={containerRef}
          className="relative flex flex-col items-center justify-center h-screen font-sans overflow-hidden bg-gradient-to-b from-blue-50 to-blue-100"
          style={{ position: 'relative', overflow: 'hidden' }}
      >
        {/* 金币效果容器 */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden z-10" style={{ height: '100%', width: '100%', position: 'absolute', top: 0, left: 0 }}>
          {coins.map(coin => (
              <Coin key={coin.id} style={coin.style} />
          ))}
        </div>

        {/* 顶部装饰 */}
        <div className="absolute top-0 w-full h-16 bg-gradient-to-r from-blue-500 to-cyan-500 z-0"></div>

        {/* 主卡片 */}
        <div className="w-full md:w-4/5 lg:w-3/4 xl:w-2/3 bg-white rounded-lg shadow-xl p-6 z-20 mx-auto border-t-4 border-blue-500" style={{ overflowY: 'hidden' }}>
          <h1 className="text-2xl md:text-3xl font-bold text-center mb-6 text-blue-600 flex justify-center items-center">
            <span className="bg-blue-500 text-white rounded-full w-10 h-10 flex items-center justify-center mr-3">¥</span>
            {t('title')}
          </h1>

          <div className="mb-6">
            <label className="block text-gray-700 text-sm font-bold mb-2 flex items-center">
            <span className="bg-blue-100 p-1 rounded-full mr-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </span>
              {t('yearlyIncome')}
            </label>
            <div className="flex items-center bg-gray-50 rounded-lg overflow-hidden shadow-inner">
              <span className="bg-gray-200 py-3 px-4 font-bold text-gray-600">¥</span>
              <input
                  type="text"
                  inputMode="decimal"
                  value={formattedIncome}
                  onChange={handleIncomeChange}
                  className="w-full py-3 px-4 bg-gray-50 outline-none text-gray-700 text-lg"
                  placeholder={t('inputYearlyIncome')}
              />
            </div>
          </div>

          <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-5 rounded-lg mb-6 border border-blue-200">
            <h2 className="text-xl font-semibold text-center mb-4 text-blue-700 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
              </svg>
              {t('realtimeIncomeRate')}
            </h2>
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-blue-400 hover:shadow-md transition-shadow">
                <div className="text-xs text-gray-500 mb-1 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {t('perSecond')}
                </div>
                <div className="text-lg font-bold text-blue-600">{formatCurrency(income.perSecond)}</div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-blue-400 hover:shadow-md transition-shadow">
                <div className="text-xs text-gray-500 mb-1 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {t('perMinute')}
                </div>
                <div className="text-lg font-bold text-blue-600">{formatCurrency(income.perMinute)}</div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-blue-400 hover:shadow-md transition-shadow">
                <div className="text-xs text-gray-500 mb-1 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {t('perHour')}
                </div>
                <div className="text-lg font-bold text-blue-600">{formatCurrency(income.perHour)}</div>
              </div>
              <div className="bg-white p-4 rounded-lg shadow-sm border-l-4 border-blue-400 hover:shadow-md transition-shadow">
                <div className="text-xs text-gray-500 mb-1 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  {t('perDay')}
                </div>
                <div className="text-lg font-bold text-blue-600">{formatCurrency(income.perDay)}</div>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-green-50 to-emerald-100 p-5 rounded-lg mb-6 border border-green-200">
            <h2 className="text-xl font-semibold text-center mb-2 text-green-700 flex items-center justify-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
              </svg>
              {t('earnedSoFar')}
            </h2>
            <div className="text-center bg-white p-4 rounded-lg shadow-inner mb-2">
              <div className="text-3xl font-bold text-green-600 mb-1">{formatCurrency(earnedSoFar)}</div>
              <div className="inline-flex items-center text-sm text-gray-500 bg-gray-100 px-3 py-1 rounded-full">
                {isRunning ? (
                    <>
                      <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
                      {t('timing')}
                    </>
                ) : (
                    <>
                      <span className="w-2 h-2 bg-yellow-500 rounded-full mr-2"></span>
                      {t('paused')}
                    </>
                )}
              </div>
            </div>
          </div>

          {/* 控制按钮 - 桌面版 */}
          <div className="hidden md:flex justify-center space-x-4">
            <button
                onClick={toggleTimer}
                className={`px-6 py-3 rounded-lg font-bold text-white flex items-center ${isRunning ? 'bg-yellow-500 hover:bg-yellow-600' : 'bg-green-500 hover:bg-green-600'} transition-colors shadow-md hover:shadow-lg`}
            >
              {isRunning ? (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {t('pause')}
                  </>
              ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {t('resume')}
                  </>
              )}
            </button>
            <button
                onClick={resetTimer}
                className="bg-blue-500 hover:bg-blue-600 text-white font-bold py-3 px-6 rounded-lg flex items-center shadow-md hover:shadow-lg transition-colors"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {t('reset')}
            </button>
          </div>

          {/* 移动端固定按钮 */}
          <div className="md:hidden fixed bottom-4 left-0 right-0 flex justify-center space-x-4 z-50 px-4">
            <div className="flex justify-center space-x-4 bg-white bg-opacity-90 p-2 rounded-full shadow-xl border border-gray-200 backdrop-blur-sm">
            <button
                onClick={toggleTimer}
                className={`px-5 py-3 rounded-lg font-bold text-white flex items-center ${isRunning ? 'bg-yellow-500' : 'bg-green-500'} shadow-lg`}
            >
              {isRunning ? (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 9v6m4-6v6m7-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {t('pause')}
                  </>
              ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    {t('resume')}
                  </>
              )}
            </button>
            <button
                onClick={resetTimer}
                className="bg-blue-500 text-white font-bold py-3 px-5 rounded-lg flex items-center shadow-lg"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              {t('reset')}
            </button>
            </div>
          </div>
        </div>

        {/* 底部装饰 */}
        <div className="absolute bottom-0 w-full h-16 bg-gradient-to-r from-blue-500 to-cyan-500 z-0"></div>

        {/* 移动端底部空间 - 防止按钮遮挡内容 */}
        <div className="md:hidden h-20"></div>
      </div>
  );
};

export default IncomeCalculator;